/**
 * NEXIRT Copilot — shared POST helper (Rev 25 Phase 2 Loop B).
 *
 * Both the hero composer (inline-response surface) and the floating
 * ChatWidget call /v1/chat/message through this helper so retry +
 * error semantics stay identical across surfaces.
 *
 * Rev 25.4: Authorization header attached when a Cognito session is
 * available. Backend resolveAuthTier (services/api-chat) verifies the
 * idToken against pool us-east-1_cZXWU4sCb and resolves the caller's
 * tier (anonymous / authed-free / authed-care). Without this header
 * every POST was classified as anonymous and signed-in admins hit the
 * 5-lifetime cap.
 */

import { fetchAuthSession } from "aws-amplify/auth";
import { authReady } from "./auth";

const ENDPOINT =
  (import.meta.env.VITE_API_BASE ?? "https://api.nexirt.com") + "/v1/chat/message";

async function authHeader(): Promise<Record<string, string>> {
  if (!authReady) return {};
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export type CopilotReply = {
  reply: string;
  sessionId: string;
  leadCaptured?: boolean;
};

export class CopilotError extends Error {
  status: number;
  code:
    | "rate_limit"
    | "upstream_error"
    | "auth_required"
    | "capacity"
    | "network"
    | "unknown";

  constructor(status: number, code: CopilotError["code"], message: string) {
    super(message);
    this.name = "CopilotError";
    this.status = status;
    this.code = code;
  }
}

export async function sendCopilotMessage(opts: {
  sessionId: string;
  message: string;
  signal?: AbortSignal;
  /**
   * Hidden honeypot field. Real users never see the composer's hidden
   * textarea; bots crawling the form fill every field. Server-side, a
   * non-empty honeypot ACKs with a fake reply and never calls Anthropic.
   * Default empty.
   */
  honeypot?: string;
  /**
   * Cloudflare Turnstile token (Rev 25 Phase 3 Loop P3-D, Rev 26.5 swap
   * to optional verified-human bump). Frontend captures from the
   * Managed-mode widget callback + forwards here. Backend treats as
   * optional: present + verifies = 2x anon limit bump; missing or
   * invalid = Rev 26.4 baseline (no penalty).
   */
  turnstileToken?: string | null;
}): Promise<CopilotReply> {
  let response: Response;
  try {
    // Rev 26.5.1 — Turnstile token ships in the JSON body only.
    // Sending it as `x-turnstile-token` ALSO put that header in
    // Access-Control-Request-Headers for every preflight; the API's
    // AllowHeaders list didn't include it, so the preflight short-
    // circuited with no ACAO and the browser killed every chat POST.
    // The backend's header-fallback read path (Phase 3 vintage) stays
    // in place server-side for non-browser callers (curl, server-to-
    // server), but the SPA never sends the header now.
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(await authHeader()),
    };
    response = await fetch(ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({
        sessionId: opts.sessionId,
        message: opts.message,
        honeypot: opts.honeypot ?? "",
        ...(opts.turnstileToken ? { cfTurnstileToken: opts.turnstileToken } : {}),
      }),
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new CopilotError(
      0,
      "network",
      "Looks like a connection hiccup. Try once more.",
    );
  }

  if (response.ok) {
    return (await response.json()) as CopilotReply;
  }

  if (response.status === 401) {
    throw new CopilotError(
      401,
      "auth_required",
      "I'm here for quick scoping conversations — let me put you in touch with our team directly.",
    );
  }
  if (response.status === 429) {
    throw new CopilotError(
      429,
      "rate_limit",
      "I'm a little busy — try again in a moment, or reach out via /contact.",
    );
  }
  // Rev 26.4 — daily Anthropic spend kill-switch returns 503
  // {code:"capacity"}. Surface a friendly message instead of the
  // generic "upstream_error" wording.
  if (response.status === 503) {
    let serverMsg: string | undefined;
    try {
      const body = (await response.json()) as { code?: string; error?: string };
      if (body.code === "capacity") serverMsg = body.error;
    } catch {
      /* fall through to default 503 message */
    }
    throw new CopilotError(
      503,
      "capacity",
      serverMsg ?? "AI service is temporarily at capacity. Please sign in to continue, or try again tomorrow.",
    );
  }
  throw new CopilotError(
    response.status,
    "upstream_error",
    "Something's off on my end — try once more, or reach out via /contact.",
  );
}
